import React from "react";
import useApi from "../../hooks/useApi";
import CommunicationHubApis from "../../services/api/CommunicationHub";
import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Typography,
  capitalize,
} from "@mui/material";
import GoBack from "./GoBack";
import AssignDialog from "./AssignDialog";
import ToggleActive from "./ToggleActive";
import useOptions from "./hooks/useOptions";
import { useHistory } from "react-router-dom";
import { useLocation } from "react-router-dom";
import ElementForm from "./ElementForm";
import LoadingComponent from "./LoadingComponent";
import ErrorComponent from "./ErrorComponent";
import MenuNames from "../../constants/Menu/MenuNames.json";
import MenuActionsMappings from "../../constants/Menu/MenuActionsMappings";
import Permission from "../Common/Permission";
import { pathMappings, pathMappingsReverse } from "./constants";
import ReactPlayer from "react-player";
import FormWrapper from "./forms/Form";
import RenderIf from "../Common/RenderIf/RenderIf";
import QuizComponent from "../Courses/CourseForm/QuizComponent";
import { unParseAssessment } from "./forms/Context/CreateElementContext";
import SequenceDetails from "./SequenceDetails";

const Details = () => {
  const { tag, id } = useParams();
  const history = useHistory();
  const { pathname, search } = useLocation();

  const allActions = MenuActionsMappings[MenuNames.COMMUNICATION_HUB];

  const element_type = pathMappings[tag];
  const { data, isLoading, error, refetch } = useApi({
    queryKey: [tag, id],
    queryFn: () =>
      CommunicationHubApis.getDetail({
        element_type,
        element_id: id,
      }),
    enabled: id && tag,
    initialData: {},
  });

  const {
    options,
    error: optionsError,
    isLoading: isOptionsLoading,
  } = useOptions();

  if (isLoading || isOptionsLoading) return <LoadingComponent />;
  if (error || optionsError) return <ErrorComponent />;

  if (search && search.includes("edit=true")) {
    const parsedData = {
      ...data,
      tags: options?.tags?.filter((tag) => data?.tags?.includes(tag.value)),
    };

    return (
      <FormWrapper
        options={options}
        initialData={parsedData}
        edit={true}
        elementType={pathMappings[tag]}
      />
    );
  }
  return (
    <Box>
      <Box display={"flex"} mb={2} justifyContent={"space-between"} gap={2}>
        <Box>
          <GoBack
            title={capitalize(`${pathMappingsReverse[element_type]} element`)}
          />
        </Box>
        <Box>
          <Permission requiredPerms={[allActions.COMMUNICATION_HUB_CREATE]}>
            <ToggleActive
              data={data}
              isActive={data?.is_active}
              elementType={pathMappings[tag]}
              callback={refetch}
            />
            <Button
              color="error"
              onClick={() => history.replace(pathname + "?edit=true")}
            >
              Edit
            </Button>
          </Permission>
          <RenderIf condition={tag === "video"}>
            <Permission requiredPerms={[allActions.COMMUNICATION_HUB_ASSIGN]}>
              <AssignDialog
                options={options}
                elementId={id}
                elementType={element_type}
              />
            </Permission>
            <RenderIf condition={element_type === "COMMUNICATION_TYPE_VIDEO"}>
              <Button
                color="error"
                onClick={() =>
                  history.push(
                    `/communication-hub/history/${tag}/${id}?title=${data?.name}`
                  )
                }
              >
                History
              </Button>
            </RenderIf>
          </RenderIf>
        </Box>
      </Box>
      <Box display={"flex"} flexDirection={"column"} gap={2}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            display: "flex",
            gap: 3,
            flexDirection: "column",
            borderRadius: 4,
          }}
        >
          <Box display={"flex"} gap={2}>
            <Box flex={7}>
              <Typography variant="h5">{data?.name}</Typography>
              <Typography variant="body2" color={"GrayText"}>
                Last edited on {data?.updated_at} by {data?.updated_by}
              </Typography>
            </Box>
            <RenderIf condition={element_type === "COMMUNICATION_TYPE_VIDEO"}>
              <Box
                display={"flex"}
                gap={2}
                alignItems={"center"}
                flex={1}
                p={1.5}
                bgcolor={"#ffebef"}
                borderRadius={2}
                sx={{
                  "& > h5": {
                    fontSize: "18px",
                  },
                }}
              >
                <Typography
                  variant="h5"
                  color={"GrayText"}
                  sx={{ flex: 1, fontWeight: 300 }}
                >
                  Views{" "}
                  <span
                    style={{ color: "black", fontWeight: 400, marginLeft: 4 }}
                  >
                    {data?.view_count ?? 0}
                  </span>
                </Typography>
                <Divider orientation="vertical" flexItem />
                <Typography
                  variant="h5"
                  color={"GrayText"}
                  sx={{ flex: 1, fontWeight: 300 }}
                >
                  Likes{" "}
                  <span
                    style={{ color: "black", fontWeight: 400, marginLeft: 4 }}
                  >
                    {data?.like_count ?? 0}
                  </span>
                </Typography>
              </Box>
            </RenderIf>
          </Box>
          <Grid container gap={2}>
            <RenderIf condition={element_type === "COMMUNICATION_TYPE_VIDEO"}>
              <Grid
                item
                sm={12}
                lg={7}
                display={"flex"}
                flexDirection={"column"}
                gap={1}
                sx={{
                  "& > div > h6": {
                    fontWeight: 500,
                    fontSize: "16px",
                    lineHeight: "20px",
                    color: "GrayText",
                  },
                }}
              >
                <Box display={"flex"} gap={2} alignItems={"center"}>
                  <Typography variant="h6" sx={{ flex: 1.5 }}>
                    Video tags
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ flex: 2, display: "flex", gap: 1 }}
                  >
                    {options?.tags
                      ?.filter((option) =>
                        (data?.tags ?? [])?.includes(option.value)
                      )
                      .map((item) => (
                        <Chip
                          key={item.value}
                          label={item.label}
                          color="info"
                        />
                      ))}
                  </Typography>
                </Box>
                <Box display={"flex"} gap={2} alignItems={"center"}>
                  <Typography variant="h6" sx={{ flex: 1.5 }}>
                    Video priority
                  </Typography>
                  <Typography variant="body2" sx={{ flex: 2 }}>
                    {
                      options?.priority?.find(
                        (option) => data?.priority === option.value
                      )?.label
                    }
                  </Typography>
                </Box>
                <Box display={"flex"} gap={2} alignItems={"center"}>
                  <Typography variant="h6" sx={{ flex: 1.5 }}>
                    Mandatory Video completion %
                  </Typography>
                  <Typography variant="body2" sx={{ flex: 2 }}>
                    {data?.pass_percentage}
                  </Typography>
                </Box>
                <Box display={"flex"} gap={2} alignItems={"center"}>
                  <Typography variant="h6" sx={{ flex: 1.5 }}>
                    Mandatory Video deadline buffer
                  </Typography>
                  <Typography variant="body2" sx={{ flex: 2 }}>
                    {data?.buffer_hours}
                  </Typography>
                </Box>
                <Box display={"flex"} gap={2} alignItems={"center"}>
                  <Typography variant="h6" sx={{ flex: 1.5 }}>
                    Keywords
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ flex: 2, display: "flex", gap: 1 }}
                  >
                    {data?.search_keywords?.map((item) => (
                      <Chip
                        key={item}
                        label={item}
                        variant="outlined"
                        color="error"
                      />
                    ))}
                  </Typography>
                </Box>
              </Grid>
            </RenderIf>
            <RenderIf condition={element_type === "ASSESSMENT"}>
              <Grid
                item
                sm={12}
                lg={7}
                display={"flex"}
                flexDirection={"column"}
                gap={1}
                sx={{
                  "& > div > h6": {
                    fontWeight: 500,
                    fontSize: "16px",
                    lineHeight: "20px",
                    color: "GrayText",
                  },
                }}
              >
                <Box display={"flex"} gap={2} alignItems={"center"}>
                  <Typography variant="h6" sx={{ flex: 1.5 }}>
                    Assessment Type
                  </Typography>
                  <Typography variant="body2" sx={{ flex: 2 }}>
                    {
                      options?.assessment_types?.find(
                        (option) => data?.assessment_type === option.value
                      )?.label
                    }
                  </Typography>
                </Box>
                <Box display={"flex"} gap={2} alignItems={"center"}>
                  <Typography variant="h6" sx={{ flex: 1.5 }}>
                    Mandatory quiz completion %
                  </Typography>
                  <Typography variant="body2" sx={{ flex: 2 }}>
                    {data?.pass_percentage}
                  </Typography>
                </Box>
                <Box display={"flex"} gap={2} alignItems={"center"}>
                  <Typography variant="h6" sx={{ flex: 1.5 }}>
                    Mandatory deadline buffer
                  </Typography>
                  <Typography variant="body2" sx={{ flex: 2 }}>
                    {data?.buffer_hours}
                  </Typography>
                </Box>
                <Box display={"flex"} gap={2} alignItems={"center"}>
                  <Typography variant="h6" sx={{ flex: 1.5 }}>
                    Randomize questions
                  </Typography>
                  <Typography variant="body2" sx={{ flex: 2 }}>
                    {data?.randomize_questions_count
                      ? `Yes(${data?.randomize_questions_count})`
                      : "No"}
                  </Typography>
                </Box>
              </Grid>
            </RenderIf>
            <RenderIf condition={element_type === "SERIES"}>
              <Grid
                item
                sm={12}
                lg={7}
                display={"flex"}
                flexDirection={"column"}
                gap={1}
                sx={{
                  "& > div > h6": {
                    fontWeight: 500,
                    fontSize: "16px",
                    lineHeight: "20px",
                    color: "GrayText",
                  },
                }}
              >
                <Box display={"flex"} gap={2} alignItems={"center"}>
                  <Typography variant="h6" sx={{ flex: 1.5 }}>
                    Priority:
                  </Typography>
                  <Typography variant="body2" sx={{ flex: 2 }}>
                    {
                      options?.priority?.find(
                        (option) => data?.priority === option.value
                      )?.label
                    }
                  </Typography>
                </Box>
                <Box display={"flex"} gap={2} alignItems={"center"}>
                  <Typography variant="h6" sx={{ flex: 1.5 }}>
                    Mandatory deadline buffer (Hr):
                  </Typography>
                  <Typography variant="body2" sx={{ flex: 2 }}>
                    {data?.buffer_hours}
                  </Typography>
                </Box>
                <Box
                  display={"flex"}
                  gap={2}
                  alignItems={"center"}
                  flexWrap={"wrap"}
                >
                  {data?.content?.map((item, index) => (
                    <Box py={2}>
                      <Typography
                        variant="body2"
                        sx={{
                          flex: 2,
                          textTransform: "uppercase",
                          letterSpacing: 3,
                        }}
                      >
                        LANGUAGE:{" "}
                        {
                          options?.languages?.find(
                            (option) => item?.lang_code === option.value
                          )?.label
                        }
                        {index === 0 && "(DEFAULT)"}
                      </Typography>
                      <Typography variant="body2" sx={{ flex: 1.5 }}>
                        {item?.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ flex: 1.5, color: "grey" }}
                      >
                        {item?.description}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Grid>
            </RenderIf>

            <Grid item xs={12} lg={4} display={"flex"} gap={2}>
              <Box>
                <img
                  src={data?.thumbnail?.portrait}
                  alt="portrait"
                  style={{ width: "100%", borderRadius: 8, maxHeight: 200 }}
                />
                <Typography variant="body2">Portrait</Typography>
              </Box>
              <Box>
                <img
                  src={data?.thumbnail?.landscape}
                  alt="landscape"
                  style={{ width: "100%", borderRadius: 8, maxHeight: 200 }}
                />
                <Typography variant="body2">Landscape</Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
        <RenderIf condition={element_type === "COMMUNICATION_TYPE_VIDEO"}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              display: "flex",
              gap: 3,
              flexDirection: "column",
              borderRadius: 4,
            }}
          >
            <Grid container gap={2}>
              {data?.content?.map((item, index) => (
                <Grid item sx={12} lg={5} key={item.lang_code}>
                  <Typography
                    variant="h6"
                    letterSpacing={"2px"}
                    sx={{ color: "#363636" }}
                  >
                    LANGUAGE:{" "}
                    {(options?.languages ?? [])?.find(
                      (lang) => lang.value === item.lang_code
                    )?.label ?? item.lang_code}{" "}
                    {index === 0 && "(DEFAULT)"}
                  </Typography>
                  <Box sx={{ color: "GrayText", mb: 1 }}>
                    <Typography variant="body1">{item.title}</Typography>
                    <Typography variant="body2">{item.description}</Typography>
                    <Typography variant="body2">
                      Video duration (mm:ss): {item.duration}
                    </Typography>
                  </Box>
                  <ReactPlayer
                    controls={true}
                    className="react-player"
                    width="100%"
                    height="300px"
                    url={item.url}
                    style={{ width: "100%", borderRadius: 8 }}
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>
        </RenderIf>
        <RenderIf condition={element_type === "ASSESSMENT"}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              display: "flex",
              gap: 3,
              flexDirection: "column",
              borderRadius: 4,
            }}
          >
            <Grid container gap={2}>
              {data?.content?.map((item, index) => (
                <Grid item sx={12} key={item.lang_code}>
                  <Typography
                    variant="h6"
                    letterSpacing={"2px"}
                    sx={{ color: "#363636", mb: 2 }}
                  >
                    LANGUAGE:{" "}
                    {(options?.languages ?? [])?.find(
                      (lang) => lang.value === item.lang_code
                    )?.label ?? item.lang_code}{" "}
                    {index === 0 && "(DEFAULT)"}
                  </Typography>
                  <Typography variant="body2" sx={{ flex: 1.5 }}>
                    {item?.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ flex: 1.5, color: "grey", mb: 2 }}
                  >
                    {item?.description}
                  </Typography>

                  <QuizComponent
                    secIndex={index}
                    handleSectionChange={() => {}}
                    handleQuizQuestionAddition={() => {}}
                    handleQuizQuestionDelete={() => {}}
                    handleQuizOptionAddition={() => {}}
                    handleOptionDelete={() => {}}
                    disableQuestionEditing={() => true}
                    items={
                      unParseAssessment({
                        initialData: { questions: [], ...item },
                      })?.questions ?? []
                    }
                    type={data.assessment_type?.toLocaleLowerCase()}
                    singleChoiceValue="SINGLE_CHOICE"
                    multipleChoiceValue="MULTIPLE_CHOICE"
                    mode="view"
                    showAudioLink
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>
        </RenderIf>
        <RenderIf condition={element_type === "SERIES"}>
          <Box
            sx={{
              display: "flex",
              gap: 3,
              flexWrap: "wrap",
              borderRadius: 4,
              "& > div": {
                maxWidth: "calc(50% - 12px)",
                minWidth: "calc(50% - 12px)",
              },
            }}
          >
            {data?.sequence_details?.map((item, index) => (
              <SequenceDetails {...item} key={index} index={index} />
            ))}
          </Box>
        </RenderIf>
      </Box>
    </Box>
  );
};

export default Details;
